import { blockRulesIsVisible } from '@ContractBuilder/rules/block';
import type { DocumentContext } from '@ContractBuilder/utils/get-document-context';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import { isBlockOrInlineAttachment } from '@features/attachments';

import type { PageItem } from '../types';

import { getPageParts, hasPageBreak } from './page-breaks';

interface PaginateOptions {
  context: DocumentContext;
  editingBlockId?: string;
  excludedBlockIds: string[];
  previewedVariationBlockIds: string[];
}

const affectsLayout = (block: ResourceBlock, { context, editingBlockId, excludedBlockIds }: PaginateOptions) =>
  block.id !== editingBlockId &&
  !excludedBlockIds.includes(block.id) &&
  blockRulesIsVisible(block, context) &&
  isBlockOrInlineAttachment(block) === 'block';

const canSplit = (block: ResourceBlock, { previewedVariationBlockIds }: PaginateOptions) =>
  !previewedVariationBlockIds.includes(block.id) && !block.isDeleted && hasPageBreak(block.content ?? '');

export const paginate = (blocks: ResourceBlock[], options: PaginateOptions): PageItem[][] => {
  const pages: PageItem[][] = [[]];
  const currentItems = () => pages[pages.length - 1];
  const breakPage = () => pages.push([]);

  blocks.forEach((block, order) => {
    const isLayoutBlock = affectsLayout(block, options);
    const isSplit = isLayoutBlock && canSplit(block, options);
    const hasBreakAfter = isLayoutBlock && block.shouldInsertPageBreak;

    if (!isSplit && !hasBreakAfter) {
      currentItems().push({ kind: 'block', block, order });
      return;
    }

    const parts = isSplit ? getPageParts(block.content, hasBreakAfter) : [block.content];
    let hasSegment = false;

    parts.forEach((content, idx) => {
      if (idx > 0) breakPage();
      const isLast = idx === parts.length - 1;

      if (content || (isLast && !hasSegment)) {
        const segment = { content, isFirst: !hasSegment, withCreator: isLast && !hasBreakAfter, index: idx };
        currentItems().push({ kind: 'block', block, order, segment });
        hasSegment = true;
      } else if (isLast && !hasBreakAfter) {
        currentItems().push({ kind: 'add-block', block, order });
      }
    });

    if (hasBreakAfter) {
      currentItems().push({ kind: 'add-block', block, order });
      breakPage();
    }
  });

  return pages;
};
